/*
 * Display mapping for the sales task form: chip tones, header pieces and
 * timeline colours. Pure so the form's look is decided in one tested place.
 */
import rule_config_line from './rule_line';
import type { Tone } from './ui';
import type { HoldPhase } from './claim_hold';

// Header chip: "Lead · L3", "L2", "Lead"; '' when neither applies.
export const kind_level_label = (is_lead: boolean, level: string | null | undefined): string =>
  [is_lead ? 'Lead' : '', level || ''].filter(Boolean).join(' · ');

// The rule line minus its leading task label ("Day 1 · 21 october 2026"), so the
// label can be bold and the rest muted. Same parts as rule_config_line.
export const rule_line_rest = (
  task_label: string | null | undefined,
  rule_day: number | null | undefined,
  created: string | null | undefined,
  task_type: string | null | undefined
): string => {
  const head = task_label || '—';
  const line: string = rule_config_line(task_label, rule_day, created, task_type);
  if (!line.startsWith(`${head} - `)) return '';
  return line
    .slice(head.length + 3)
    .split(' - ')
    .join(' · ');
};

// Finished = success, cancelled / no-show / replaced = error, anything else is upcoming.
export const session_status_tone = (status: string | null | undefined): Tone => {
  const s = (status || '').toLowerCase();
  if (s.includes('finish')) return 'success';
  if (s.includes('cancel') || s.includes('no show') || s.includes('replacement')) return 'error';
  return 'info';
};

const COMM_KINDS: Record<string, { label: string; tone: Tone }> = {
  chat: { label: 'Chat', tone: 'info' },
  sms: { label: 'SMS', tone: 'success' },
  whatsapp: { label: 'WhatsApp', tone: 'success' },
  call: { label: 'Call', tone: 'warning' },
  session: { label: 'Session', tone: 'brand' },
  note: { label: 'Note', tone: 'neutral' }
};

export const comm_kind_chip = (kind: string | null | undefined): { label: string; tone: Tone } =>
  COMM_KINDS[kind || ''] || { label: kind || '—', tone: 'neutral' };

export const sentiment_tone = (sentiment: string | null | undefined): Tone => {
  const s = (sentiment || '').toLowerCase();
  if (s === 'positive') return 'success';
  if (s === 'negative') return 'error';
  return 'neutral';
};

// Membership timeline marker colour per event kind (theme tokens).
const TIMELINE_COLOR: Record<string, string> = {
  account_created: 'var(--text-muted)',
  membership_signup: 'var(--state-success)',
  plan_change: 'var(--state-info-solid)',
  pause: 'var(--state-warning)',
  trial_booking: 'var(--state-info)',
  trial_join: 'var(--state-success-text)',
  trial_reset: 'var(--brand-secondary)',
  trial_end: 'var(--state-error)',
  membership_cancelled: 'var(--state-error-text)'
};

export const timeline_color = (type: string | null | undefined): string =>
  TIMELINE_COLOR[type || ''] || 'var(--text-muted)';

// Held-by-you banner: amber while there is time, red in the last minute or once lapsed.
export const hold_banner_tone = (phase: HoldPhase | 'untimed'): 'warning' | 'error' =>
  phase === 'critical' || phase === 'expired' ? 'error' : 'warning';
